import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { Negotiation } from "../../api/types";
import { S } from "../../styles";

export function SimulateSupplierResponsePanel({
  neg,
  onResponded,
}: {
  neg: Negotiation;
  onResponded: () => void;
}) {
  const lastAgentTurn = [...neg.transcript]
    .reverse()
    .find((t) => t.role === "agent");
  const lastSupplierTurn = [...neg.transcript]
    .reverse()
    .find((t) => t.role === "supplier");

  let suggestedPrice: number;
  if (
    lastAgentTurn?.proposed_price != null &&
    lastSupplierTurn?.proposed_price != null
  ) {
    const gap = lastSupplierTurn.proposed_price - lastAgentTurn.proposed_price;
    suggestedPrice = parseFloat(
      (gap > 0
        ? lastSupplierTurn.proposed_price - gap * 0.4
        : lastSupplierTurn.proposed_price * 0.99
      ).toFixed(2),
    );
  } else if (lastAgentTurn?.proposed_price != null) {
    suggestedPrice = parseFloat(
      (lastAgentTurn.proposed_price * 1.05).toFixed(2),
    );
  } else {
    suggestedPrice = parseFloat((neg.initial_unit_price * 0.97).toFixed(2));
  }

  const defaultMsg =
    lastSupplierTurn != null
      ? `Thank you for your revised offer of $${lastAgentTurn?.proposed_price?.toFixed(2)}. ` +
        `We have reviewed your proposal and can come down to $${suggestedPrice.toFixed(2)} ` +
        `per unit. This reflects a meaningful concession on our end and is the best ` +
        `we can do given current supply costs. We hope this allows us to close the deal.`
      : lastAgentTurn?.proposed_price != null
        ? `Thank you for your offer of $${lastAgentTurn.proposed_price.toFixed(2)}. ` +
          `We can meet you at $${suggestedPrice.toFixed(2)} per unit, reflecting our ` +
          `current material costs. This is our best achievable price given lead time ` +
          `constraints. Please advise if this works for your procurement team.`
        : `We can provide this part at $${suggestedPrice.toFixed(2)} per unit with a ` +
          `10-day lead time and full trace documentation included. Please confirm ` +
          `your order to proceed.`;

  const [price, setPrice] = useState(String(suggestedPrice));
  const [content, setContent] = useState(defaultMsg);

  const injectMutation = useMutation({
    mutationFn: () =>
      api.injectSupplierResponse(
        neg.id,
        content.trim() || defaultMsg,
        price ? parseFloat(price) : undefined,
      ),
    onSuccess: () => onResponded(),
  });

  if (injectMutation.isSuccess) {
    return (
      <div
        style={{
          marginTop: 10,
          padding: "8px 12px",
          background: "#f3e5f5",
          borderRadius: 6,
          fontSize: 12,
          color: "#6a1b9a",
          fontWeight: 600,
        }}
      >
        ✓ Supplier response injected — waiting for agent to react…
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 12,
        borderTop: "1px dashed #ce93d8",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase" as const,
          color: "#9c27b0",
          letterSpacing: 0.5,
          marginBottom: 10,
        }}
      >
        Simulate Supplier Response (Dev / Test)
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 8,
          flexWrap: "wrap" as const,
        }}
      >
        <div>
          <label style={{ ...S.label, fontSize: 12 }}>
            Proposed Price (USD)
          </label>
          <input
            style={{ ...S.input, width: 130, marginBottom: 0, fontSize: 13 }}
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={{ ...S.label, fontSize: 12 }}>
            Supplier Message
            <span style={{ ...S.muted, fontWeight: 400, marginLeft: 6 }}>
              (auto-generated from transcript — edit if needed)
            </span>
          </label>
          <textarea
            style={{
              width: "100%",
              padding: "8px 10px",
              border: "1px solid #d0d4e0",
              borderRadius: 6,
              fontSize: 13,
              boxSizing: "border-box" as const,
              resize: "vertical" as const,
              minHeight: 72,
              fontFamily: "system-ui, sans-serif",
              lineHeight: 1.45,
            }}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <button
        style={{
          ...S.btnSm,
          background: "#9c27b0",
          fontSize: 12,
          opacity: injectMutation.isPending ? 0.6 : 1,
        }}
        onClick={() => injectMutation.mutate()}
        disabled={injectMutation.isPending}
      >
        {injectMutation.isPending
          ? "Injecting…"
          : "📨 Simulate Supplier Response"}
      </button>

      {injectMutation.error && (
        <p style={{ ...S.err, marginTop: 6, fontSize: 12 }}>
          ⚠ {injectMutation.error.message}
        </p>
      )}
    </div>
  );
}
